/**
 * Central events → BeHeard RTK cache invalidation. Event-driven, never polled.
 *
 * Central owns access (membership, role, capabilities, entitlements, flags)
 * and pushes changes on its event stream (/api/events/stream, opened ONCE by
 * NotificationProvider). BeHeard's reads that are derived from that access
 * were cached until their timer lapsed (up to LOW_CHURN_CACHE_SECONDS), so an
 * administrator's change was invisible in an open tab. Each event now
 * invalidates exactly the tags whose server answer it can change; RTK refetches
 * only the queries currently mounted, the rest on next use.
 *
 *   workspace.member.added/removed/updated.v1   membership / role changed
 *     → Capabilities, User (onboarding profile, me, gamification overview),
 *       PublicProfile, Challenges, TrainingAssignments, Organizations,
 *       OrgMembers, DashboardSummary
 *   entitlements.changed.v1, subscription.status.changed.v1,
 *   feature.flags.changed.v1                    what the workspace may use
 *     → Capabilities, User, Challenges, TrainingAssignments, DashboardSummary
 *   credits.balance.changed.v1                  no BeHeard read derives from
 *     the balance (Central's billing UI owns it) → nothing
 *
 * Workspace switch and logout are not events here: they already reset the
 * WHOLE API cache (useWorkspace, useCentralAuth), and the stream is reopened
 * for the new workspace. An event naming a different workspace is ignored.
 * After a RECONNECT, events sent while disconnected were missed, so every
 * mapped tag is invalidated once — a catch-up, not a poll.
 *
 * Central's stream client delivers only its known business event types; a
 * per-user "access changed" push (as CRM sends over its own socket) does not
 * reach BeHeard today. Server-side, see docs/CENTRAL-INTEGRATION.md.
 */

import type { CentralBusinessEventType, CentralEventPayload } from '@levelup/central-events';
import type { Dispatch } from '@reduxjs/toolkit';
import { api } from '../api';

const MEMBERSHIP_TAGS = [
  'Capabilities',
  'User',
  'PublicProfile',
  'Challenges',
  'TrainingAssignments',
  'Organizations',
  'OrgMembers',
  'DashboardSummary',
] as const;

const ENTITLEMENT_TAGS = ['Capabilities', 'User', 'Challenges', 'TrainingAssignments', 'DashboardSummary'] as const;

export const CENTRAL_EVENT_INVALIDATIONS: Readonly<Record<CentralBusinessEventType, readonly string[]>> = Object.freeze({
  'workspace.member.added.v1': MEMBERSHIP_TAGS,
  'workspace.member.removed.v1': MEMBERSHIP_TAGS,
  'workspace.member.updated.v1': MEMBERSHIP_TAGS,
  'entitlements.changed.v1': ENTITLEMENT_TAGS,
  'subscription.status.changed.v1': ENTITLEMENT_TAGS,
  'feature.flags.changed.v1': ENTITLEMENT_TAGS,
  'credits.balance.changed.v1': [],
});

/** Every tag any event can invalidate: what a reconnect catches up on. */
export const ALL_CENTRAL_EVENT_TAGS: readonly string[] = Object.freeze(
  [...new Set(Object.values(CENTRAL_EVENT_INVALIDATIONS).flat())]
);

/** The tags one event invalidates; none for another workspace's event or an unknown type. */
export function tagsForCentralEvent(
  eventType: string,
  payload: CentralEventPayload | null | undefined,
  currentWorkspaceId: string | null | undefined
): readonly string[] {
  const forWorkspace = payload?.workspaceId;
  if (typeof forWorkspace === 'string' && currentWorkspaceId && forWorkspace !== currentWorkspaceId) return [];
  return (CENTRAL_EVENT_INVALIDATIONS as Record<string, readonly string[]>)[eventType] ?? [];
}

function invalidate(dispatch: Dispatch, tags: readonly string[]): void {
  if (tags.length === 0) return;
  // Tags registered by other slices (Challenges, DashboardSummary) are not in
  // the base api's static tag union; invalidating them is still well-defined.
  dispatch(api.util.invalidateTags(tags as unknown as Parameters<typeof api.util.invalidateTags>[0]));
}

/** Invalidate what one Central event can have changed. Returns the tags invalidated. */
export function invalidateForCentralEvent(
  dispatch: Dispatch,
  eventType: string,
  payload: CentralEventPayload | null | undefined,
  currentWorkspaceId: string | null | undefined
): readonly string[] {
  const tags = tagsForCentralEvent(eventType, payload, currentWorkspaceId);
  invalidate(dispatch, tags);
  return tags;
}

/** After a reconnect: events may have been missed while disconnected. */
export function invalidateAfterCentralReconnect(dispatch: Dispatch): void {
  invalidate(dispatch, ALL_CENTRAL_EVENT_TAGS);
}
